import { KeyboardKeyCode } from '@anecdotes/utils';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector } from '@angular/core';
import { ModalHostComponent } from '../../components/modal-host/modal-host.component';
import { MODAL_DEFINITION } from '../../inection-tokens';
import { ModalDraft, ModalWindow, ModalWindowType, ModalWindowWithSwitcher, ModalWindowWrapper } from '../../models';
import { CdkPortalService } from '../cdk-portal-service/cdk-portal.service';
import { ModalTemplateHostComponent } from '../../components/modal-template-host/modal-template-host.component';
import { Router, NavigationStart } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Injectable()
export class ModalWindowService {
  private modalWindowBase?: ModalWindowWrapper;
  private subscriptionDetacher = new Subject<void>();
  private modalsToReOpenStack: Parameters<ModalWindowService['openModal']>[] = [];

  private readonly modalDrafts = new Map<ModalDraft['draftId'], ModalDraft['payload']>();

  constructor(
    private cdkPortalService: CdkPortalService,
    private overlay: Overlay,
    private router: Router,
    private injector: Injector,
  ) {}

  /** @deprecated We shouldn't use template based modals. Will be removed within next releases */
  open(modal: ModalWindow): void {
    this.openInSwitcher({
      componentsToSwitch: [
        {
          id: 'template',
          componentType: ModalTemplateHostComponent,
        },
      ],
      context: {
        templateModalWindow: modal,
      },
    });
  }

  openInSwitcher(modal: ModalWindowWithSwitcher): void {
    this.cdkPortalService.setDirection('opening');
    this.openModal('center', modal);
  }

  openCustomSidePanel(modal: ModalWindowWithSwitcher, sidePanelWidthClass: `w-${number}`): void {
    this.openModal(sidePanelWidthClass, this.disableClosingOnEsc(modal));
  }

  openSmallSlidePanel(modal: ModalWindowWithSwitcher): void {
    this.cdkPortalService.setDirection('opening');
    this.openModal('right-1/4', this.disableClosingOnEsc(modal));
  }

  openMediumSlidePanel(modal: ModalWindowWithSwitcher): void {
    this.cdkPortalService.setDirection('opening');
    this.openModal('right-1/2', this.disableClosingOnEsc(modal));
  }

  openLargeSlidePanel(modal: ModalWindowWithSwitcher): void {
    this.cdkPortalService.setDirection('opening');
    this.openModal('right-3/4', this.disableClosingOnEsc(modal));
  }

  close(): void {
    this.cdkPortalService.setDirection('closing');
    this.cdkPortalService.closePortal();
  }

  closeModalAndClearReOpenStack(): void {
    this.clearModalsStack();
    this.close();
  }

  clearModalsStack(): void {
    this.modalsToReOpenStack = [];
  }

  getDraft(draftId: ModalDraft['draftId']): ModalDraft['payload'] | undefined {
    return this.modalDrafts.get(draftId);
  }

  setDraft(draft: ModalDraft): void {
    this.modalDrafts.set(draft.draftId, draft.payload);
  }

  clearDraft(draftId: ModalDraft['draftId']): void {
    this.modalDrafts.delete(draftId);
  }

  private disableClosingOnEsc(modal: ModalWindowWithSwitcher): ModalWindowWithSwitcher {
    const options = modal.options || {};

    return {
      ...modal,
      options: {
        ...options,
        closeOnEsc: 'closeOnEsc' in options ? options.closeOnEsc : false,
      },
    };
  }

  private cleanUp(): void {
    if (this.modalWindowBase?.modalWindow?.options?.onClose) {
      this.modalWindowBase.modalWindow.options.onClose(true);
    }

    this.subscriptionDetacher.next();

    if (!this.modalsToReOpenStack.length) {
      this.modalWindowBase = undefined;
    }

    if (this.modalsToReOpenStack.length && this.cdkPortalService.getDirection() === 'closing') {
      const [type, modalWindow] = this.modalsToReOpenStack.pop();
      this.openModal(type, modalWindow);
    }
  }

  private openModal(type: ModalWindowType, modalWindow: ModalWindowWithSwitcher): void {
    if (this.modalWindowBase && this.cdkPortalService.getDirection() === 'opening') {
      if (this.modalWindowBase?.modalWindow?.options?.reOpenOnClose) {
        this.modalsToReOpenStack.push([this.modalWindowBase.type, { ...this.modalWindowBase.modalWindow }]);
      }
    }

    this.modalWindowBase = {
      modalWindow,
      type,
    };

    const portal = new ComponentPortal(
      ModalHostComponent,
      undefined,
      Injector.create({
        providers: [
          {
            provide: MODAL_DEFINITION,
            useValue: this.modalWindowBase,
          },
        ],
        parent: this.injector,
      }),
    );

    const positionStrategy = this.overlay.position().global().centerVertically().centerHorizontally();

    const overlayConfig: OverlayConfig = {
      disposeOnNavigation: true,
      backdropClass: 'modal-window-backdrop',
      positionStrategy,
      hasBackdrop: this.modalWindowBase.modalWindow?.options?.displayBackground !== false,
    };

    const overlayRef = this.cdkPortalService.openPortal(portal, overlayConfig);
    this.setupListeners(overlayRef);
  }

  private setupListeners(overlayRef: OverlayRef): void {
    const options = this.modalWindowBase?.modalWindow.options;

    this.router.events
      .pipe(
        filter((event) => {
          return event instanceof NavigationStart && !event.url.includes(window.location.pathname);
        }),
        takeUntil(this.subscriptionDetacher),
      )
      .subscribe(this.closeModalAndClearReOpenStack.bind(this));

    if (options?.closeOnBackgroundClick) {
      overlayRef
        .backdropClick()
        .pipe(takeUntil(this.subscriptionDetacher))
        .subscribe(this.close.bind(this));
    }

    if ('closeOnEsc' in (options || {}) ? options.closeOnEsc : true) {
      overlayRef
        .keydownEvents()
        .pipe(
          filter((event: KeyboardEvent) => event.key === 'Escape' || event.keyCode === KeyboardKeyCode.Esc),
          takeUntil(this.subscriptionDetacher),
        )
        .subscribe(() => this.close());
    }

    overlayRef
      .detachments()
      .pipe(takeUntil(this.subscriptionDetacher))
      .subscribe(() => this.cleanUp());
  }
}
